<template>
  <el-card class="tree-card">
    <div class="room-tree-title">房屋结构</div>
    <el-tree
      ref="tree"
      :data="treeData"
      :props="defaultProps"
      node-key="id"
      highlight-current
      @node-click="handleNodeClick"
    >
      <span slot-scope="{ node, data }" class="custom-tree-node">
        <i :class="structureIcon(data)" class="tree-node-icon"></i>
        <span class="tree-node-label">{{ structureLabel(data, node.label) }}</span>
      </span>
    </el-tree>
  </el-card>
</template>

<script>
import { queryCommunityUnitTree, queryAdminRoomsTree } from '@/api/fee/adminRoomFeeApi'

export default {
  name: 'CommunityRoomTree',
  data() {
    return {
      treeData: [],
      defaultProps: {
        children: 'children',
        label: 'text'
      },
      loadedUnits: new Set() // Track which units have loaded their rooms
    }
  },
  mounted() {
    this.initCommunityRoomTree()
  },
  methods: {
    async initCommunityRoomTree() {
      try {
        const res = await queryCommunityUnitTree({ a: 1 })
        if (res.code === 0) {
          this.treeData = res.data
        }
      } catch (error) {
        console.error('Failed to load community tree:', error)
      }
    },

    async handleNodeClick(data) {
      // Only handle unit nodes (u_ prefix) that haven't been loaded yet
      if (data.id.startsWith('u_') && !this.loadedUnits.has(data.id)) {
        await this.loadRooms(data.unitId || data.id.replace('u_', ''))
        this.loadedUnits.add(data.id)
      }
      
      // Emit event for room selection
      if (data.id.startsWith('r_')) {
        this.$emit('select-room', {
          roomName: data.roomName,
          roomId: data.roomId
        })
      }
    },

    async loadRooms(unitId) {
      try {
        const param = {
          page: 1,
          row: 1000,
          unitId: unitId
        }

        const res = await queryAdminRoomsTree(param)
        if (res.rooms && res.rooms.length > 0) {
          const rooms = res.rooms.map(room => ({
            id: `r_${room.roomId}`,
            roomId: room.roomId,
            roomName: `${room.floorNum}-${room.unitNum}-${room.roomNum}`,
            text: room.ownerName ? `${room.roomNum}(${room.ownerName})` : room.roomNum
          }))

          // Find and update the unit node
          const unitNode = this.findNodeById(`u_${unitId}`, this.treeData)
          if (unitNode) {
            this.$set(unitNode, 'children', rooms)
            
            // Auto-select the first room if needed
            if (rooms.length > 0) {
              this.$nextTick(() => {
                this.$refs.tree.setCurrentKey(rooms[0].id)
                this.$emit('select-room', {
                  roomName: rooms[0].roomName,
                  roomId: rooms[0].roomId
                })
              })
            }
          }
        }
      } catch (error) {
        console.error('Failed to load rooms:', error)
      }
    },

    findNodeById(id, nodes) {
      if (!nodes) return null
      
      for (const node of nodes) {
        if (node.id === id) return node
        if (node.children) {
          const found = this.findNodeById(id, node.children)
          if (found) return found
        }
      }
      return null
    },
    structureLabel(data, fallback) {
      if (data.id && data.id.startsWith('f_')) return this.structureSuffix(data.floorNum || fallback, '号楼')
      if (data.id && data.id.startsWith('u_')) return this.structureSuffix(data.unitNum || fallback, '单元')
      return fallback
    },
    structureSuffix(value, suffix) {
      const text = String(value || '').trim()
      return text.endsWith(suffix) ? text : `${text}${suffix}`
    },
    structureIcon(data) {
      if (data.id && data.id.startsWith('c_')) return 'el-icon-location-outline'
      if (data.id && data.id.startsWith('f_')) return 'el-icon-office-building'
      if (data.id && data.id.startsWith('u_')) return 'el-icon-house'
      return 'el-icon-document'
    }
  }
}
</script>

<style scoped>
.tree-card {
  max-height: 80vh;
  overflow-y: auto;
}
.room-tree-title {
  padding: 0 0 8px;
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
  color: #1f2d3d;
}
.tree-card ::v-deep .el-card__body {
  padding: 12px 8px;
}
.tree-card ::v-deep .el-tree-node__content {
  height: 32px;
  margin-bottom: 2px;
  border-radius: 4px;
}
.tree-card ::v-deep .el-tree-node__content:hover {
  background: #edf4fb;
}
.tree-card ::v-deep .el-tree--highlight-current .el-tree-node.is-current > .el-tree-node__content {
  color: #fff;
  background: #149582;
}
.tree-card ::v-deep .el-tree--highlight-current .el-tree-node.is-current > .el-tree-node__content .tree-node-icon,
.tree-card ::v-deep .el-tree--highlight-current .el-tree-node.is-current > .el-tree-node__content .el-tree-node__expand-icon {
  color: #fff;
}
.custom-tree-node {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  overflow: hidden;
}
.tree-node-icon {
  margin-right: 6px;
  color: #6d86a8;
}
.tree-node-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
